/** Boot-scale pressure mat; the steel perimeter is deliberately more prominent than a domestic doormat trim. */
import * as THREE from 'three';
import { surface } from '../materials';
import { seatOnOrigin } from '../origin';
import type { AssetDefinition } from '../types';

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pressure-mat';
  const rubber = surface('organic', 0x1d2425);
  const steel = surface('metal', 0x7b8585);

  const pad = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.018, 0.62), rubber);
  pad.name = 'rubber-pad';
  pad.position.y = 0.009;
  pad.receiveShadow = true;
  group.add(pad);

  for (const [x, z, width, length, name] of [
    [0, -0.335, 0.9, 0.03, 'trim-back'], [0, 0.335, 0.9, 0.03, 'trim-front'],
    [-0.435, 0, 0.03, 0.64, 'trim-left'], [0.435, 0, 0.03, 0.64, 'trim-right'],
  ] as const) {
    const trim = new THREE.Mesh(new THREE.BoxGeometry(width, 0.025, length), steel);
    trim.name = name;
    trim.position.set(x, 0.0125, z);
    trim.castShadow = true;
    group.add(trim);
  }

  for (let index = 0; index < 11; index += 1) {
    const rib = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.007, 0.58), rubber);
    rib.name = `cross-rib-${index + 1}`;
    rib.position.set(-0.32 + index * 0.064, 0.0215, 0);
    group.add(rib);
  }

  // The tail briefly leaves the front-right corner, then bends back inside the
  // placement envelope. A straight 0.2 m lead would falsely make this a 0.9 m
  // long obstacle even though the activation pad is only 0.7 m deep.
  const cableCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0.41, 0.015, 0.3),
    new THREE.Vector3(0.43, 0.015, 0.34),
    new THREE.Vector3(0.39, 0.015, 0.33),
    new THREE.Vector3(0.43, 0.015, 0.28),
    new THREE.Vector3(0.36, 0.015, 0.27),
  ]);
  const cable = new THREE.Mesh(new THREE.TubeGeometry(cableCurve, 10, 0.007, 4, false), steel);
  cable.name = 'armoured-cable-tail';
  cable.castShadow = true;
  group.add(cable);

  // Authored from a convenient face outward, then seated on the contract's
  // origin. See `seatOnOrigin` for why five assets in this batch needed it.
  seatOnOrigin(group);

  return group;
}

export const pressureMatAsset: AssetDefinition = {
  id: 'pressure-mat',
  label: 'Pressure mat',
  description: 'Boot-scale black rubber pressure mat with steel edge trim, cross ribs, and an armoured cable tail.',
  footprint: { width: 0.9, height: 0.025, length: 0.7 },
  create: build,
};
